import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useCemeterySettings } from '../contexts/CemeterySettingsContext';
import { formatEformTombCount } from '../utils/formatters';

const ElectronicContractModal = ({ isOpen, onClose, templates, onSelectTemplate, contractData, isAdditionalBurial = false }) => {
  const { getCemeterySettings } = useCemeterySettings();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const iframeRef = useRef(null);
  const eformsignRef = useRef(null);

  // eFormSign 임베딩 설정 (프론트엔드 config에서 직접 사용)
  const embeddingConfig = {
    company_id: 'e978b93c60d54ae980e4fa7b5c0aa28a', // 공통 company ID (Access Token 응답에서 확인한 값)
    cemeteries: [
      {
        cemetery_id: 1,
        member_id: 'aonelink20151111@gmail.com'
      },
      {
        cemetery_id: 2,
        member_id: 'nhgpark1973@naver.com'
      }
    ]
  };

  useEffect(() => {
    console.log('=== eFormSign 초기화 체크 ===');
    console.log('Modal isOpen:', isOpen);

    if (!isOpen) return;

    // jQuery 로드 확인
    const checkJQuery = () => {
      if (window.jQuery) {
        console.log('✅ jQuery loaded:', window.jQuery.fn.jquery);
        return true;
      }
      console.error('❌ jQuery not loaded');
      return false;
    };

    // eFormSign 스크립트 로드 확인 및 동적 로드
    const loadEformSignScript = () => {
      return new Promise((resolve, reject) => {
        if (window.EformSignDocument) {
          console.log('✅ EformSignDocument already loaded');
          resolve();
          return;
        }

        console.log('⏳ Loading efs_embedded_v2.js...');
        const script = document.createElement('script');
        script.src = 'https://www.eformsign.com/lib/js/efs_embedded_v2.js';
        script.onload = () => {
          console.log('✅ efs_embedded_v2.js loaded successfully');
          // 스크립트 로드 후 짧은 대기
          setTimeout(() => {
            if (window.EformSignDocument) {
              resolve();
            } else {
              reject(new Error('EformSignDocument not available after script load'));
            }
          }, 100);
        };
        script.onerror = () => {
          console.error('❌ Failed to load efs_embedded_v2.js');
          reject(new Error('Failed to load eFormSign script'));
        };
        document.head.appendChild(script);
      });
    };

    // 초기화 로직
    const initEformSign = async () => {
      try {
        if (!checkJQuery()) {
          alert('jQuery가 로드되지 않았습니다. 페이지를 새로고침해주세요.');
          return;
        }

        await loadEformSignScript();

        if (!eformsignRef.current) {
          console.log('✅ Initializing EformSignDocument...');
          eformsignRef.current = new window.EformSignDocument();
          console.log('✅ EformSignDocument initialized:', eformsignRef.current);
        }
      } catch (error) {
        console.error('❌ eFormSign 초기화 실패:', error);
        alert('eFormSign 초기화에 실패했습니다: ' + error.message);
      }
    };

    initEformSign();

    // Cleanup when modal closes
    return () => {
      if (!isOpen && eformsignRef.current) {
        try {
          console.log('Closing eFormSign...');
          eformsignRef.current.close();
        } catch (e) {
          console.log('eFormSign already closed');
        }
        eformsignRef.current = null;
      }
    };
  }, [isOpen]);

  const handleSelectTemplate = (template) => {
    console.log('Template selected:', template);
    setSelectedTemplate(template);

    // 모달 닫기
    onClose();

    // eFormSign이 초기화될 때까지 대기 후 미리보기 열기
    const waitForEformSign = setInterval(() => {
      if (window.EformSignDocument && eformsignRef.current) {
        clearInterval(waitForEformSign);
        console.log('Opening document preview...');
        openDocumentPreview(template);
      } else {
        console.log('⏳ Waiting for eFormSign initialization...');
      }
    }, 100);

    // 10초 후 타임아웃
    setTimeout(() => {
      clearInterval(waitForEformSign);
      if (!eformsignRef.current) {
        console.error('❌ eFormSign initialization timeout');
        alert('eFormSign 초기화 시간이 초과되었습니다. 페이지를 새로고침해주세요.');
      }
    }, 10000);
  };

  const openDocumentPreview = async (template) => {
    console.log('openDocumentPreview called with template:', template);
    console.log('window.EformSignDocument:', window.EformSignDocument);
    console.log('eformsignRef.current:', eformsignRef.current);

    if (!window.EformSignDocument || !eformsignRef.current) {
      console.error('eFormSign not initialized');
      alert('eFormSign이 초기화되지 않았습니다. 페이지를 새로고침 후 다시 시도해주세요.');
      return;
    }

    console.log('contractData:', contractData);
    const getOrderedAcquaintances = (acquaintances = []) => {
      const withIndex = (acquaintances || []).map((acc, idx) => ({ acc, idx }));
      withIndex.sort((a, b) => {
        const aPrimary =
          a.acc?.is_primary === true ||
          a.acc?.is_primary === 1 ||
          a.acc?.is_primary === 'true' ||
          a.acc?.is_primary_contact === true ||
          a.acc?.is_primary_contact === 1;
        const bPrimary =
          b.acc?.is_primary === true ||
          b.acc?.is_primary === 1 ||
          b.acc?.is_primary === 'true' ||
          b.acc?.is_primary_contact === true ||
          b.acc?.is_primary_contact === 1;
        if (aPrimary !== bPrimary) return aPrimary ? -1 : 1;
        return a.idx - b.idx;
      });
      return withIndex.map(({ acc }) => acc);
    };

    const orderedAcquaintances = getOrderedAcquaintances(contractData?.acquaintances);
    const primaryContact = orderedAcquaintances?.[0];
    console.log('primaryContact:', primaryContact);
    const getAcquaintanceRelationshipDisplay = (acquaintance) => {
      if (!acquaintance) return '';

      const relationship = (acquaintance.relationship || '').trim();
      const relationshipOther = (
        acquaintance.relationship_other ||
        acquaintance.relationship_custom ||
        ''
      ).trim();

      if (relationship === '기타') {
        return relationshipOther || '기타';
      }

      return relationship;
    };

    // Access Token 발급
    let accessToken = '';
    let refreshToken = '';
    try {
      console.log('🔑 Fetching access token...');
      const { contractAPI } = await import('../services/api');
      const response = await contractAPI.getEFormAccessToken(contractData?.mausoleum?.cemetery_id);
      accessToken = response.data?.data?.access_token;
      refreshToken = response.data?.data?.refresh_token;
      console.log('✅ Access token received:', accessToken ? '(token exists)' : '(no token)');
      console.log('✅ Refresh token received:', refreshToken ? '(token exists)' : '(no token)');
    } catch (error) {
      console.error('❌ Failed to get access token:', error);
      alert('Access Token 발급에 실패했습니다. 로그인 화면이 나타날 수 있습니다.');
    }

    // 숫자 포맷팅 헬퍼 함수 (천단위 쉼표)
    const formatNumber = (value) => {
      const num = parseFloat(value) || 0;
      return num.toLocaleString('ko-KR');
    };

    // 묘지 설정 가져오기
    const cemeteryId = contractData?.mausoleum?.cemetery_id || contractData?.cemeteryId;
    const cemeterySettings = getCemeterySettings(cemeteryId);
    const areaSize = parseFloat(contractData?.mausoleum?.area_size) || 0;

    // Map field values similar to handleTemplateSelect in ContractFormV5
    const mapFieldValue = (fieldId) => {
      switch (fieldId) {
        case '묘적번호': return contractData?.mausoleum?.member_number || '';
        case '사망자 성명': return contractData?.dead?.[0]?.name || '';
        case '사망자 성별': return contractData?.dead?.[0]?.gender || '';
        case '사망일자': return contractData?.dead?.[0]?.death_date || '';
        case '장례일자': return contractData?.dead?.[0]?.report_date || '';
        case '사망자 주민등록번호': return contractData?.dead?.[0]?.resident_number || '';
        case '단지': return contractData?.mausoleum?.complex || '';
        case '묘역': return contractData?.mausoleum?.area || '';
        case '열': return contractData?.mausoleum?.row_number || '';
        case '호': return contractData?.mausoleum?.number || '';
        case '평형': return contractData?.mausoleum?.area_size || '';
        case '기수':
          return formatEformTombCount(contractData?.mausoleum);
        case '장례구분1': return contractData?.dead?.[0]?.burial_method_1 || '';
        case '장례구분2': return contractData?.dead?.[0]?.burial_method_2 || '';
        case '관리비 발생 주기':
        case '관리비 발생주기': {
          const period = contractData?.mausoleum?.period;
          // period가 숫자 문자열이거나 숫자인지 확인
          const periodValue = period ? (typeof period === 'string' ? parseInt(period, 10) : period) : null;
          return periodValue ? periodValue.toString() : '5';
        }
        case '사용료':
          return formatNumber(contractData?.cost?.usage_fee || 0);
        case '관리비': {
          if (isAdditionalBurial) {
            return '0';
          }
          // 크기(평수) * 계약기간 * 평당 관리비
          const managementFeePerSqm = parseFloat(cemeterySettings?.management_fee_per_sqm) || 0;
          const period = parseFloat(contractData?.mausoleum?.period) || 0;
          const calculatedManagementFee = areaSize * period * managementFeePerSqm;
          return formatNumber(calculatedManagementFee);
        }
        case '작업비':
          return formatNumber(contractData?.cost?.burial_work_fee || 0);
        case '총액': {
          // 전자계약서용 총액: 사용료 + 관리비 + 매장작업비 (+ 안치자 추가 시 설치비)
          const usageFee = parseFloat(contractData?.cost?.usage_fee || 0);
          const managementFee = isAdditionalBurial
            ? 0
            : (() => {
                const managementFeePerSqm = parseFloat(cemeterySettings?.management_fee_per_sqm) || 0;
                const period = parseFloat(contractData?.mausoleum?.period) || 0;
                return areaSize * period * managementFeePerSqm;
              })();
          const burialWorkFee = parseFloat(contractData?.cost?.burial_work_fee || 0);
          const installationFee = parseFloat(contractData?.cost?.installation_fee || 0);
          return formatNumber(usageFee + managementFee + burialWorkFee + installationFee);
        }
        case '계약 연도': return new Date(contractData?.contract_date || new Date()).getFullYear().toString();
        case '계약 월': return (new Date(contractData?.contract_date || new Date()).getMonth() + 1).toString();
        case '계약 일': return new Date(contractData?.contract_date || new Date()).getDate().toString();
        case '사망자 주소': {
          const roadAddress = contractData?.dead?.[0]?.road_address || '';
          const addressDetail = contractData?.dead?.[0]?.address_detail || '';
          return [roadAddress, addressDetail].filter(Boolean).join(', ').trim();
        }
        case '연고자1 주소': {
          const roadAddress = primaryContact?.road_address || '';
          const addressDetail = primaryContact?.detail_address || '';
          return [roadAddress, addressDetail].filter(Boolean).join(', ').trim();
        }
        case '연고자1 성명': return primaryContact?.name || '';
        case '연고자1 전화번호': return primaryContact?.phone || '';
        case '연고자1 관계': return getAcquaintanceRelationshipDisplay(primaryContact);
        case '연고자1 주민등록번호': return primaryContact?.resident_number || '';
        case '연고자2 성명': return orderedAcquaintances?.[1]?.name || '';
        case '연고자2 전화번호': return orderedAcquaintances?.[1]?.phone || '';
        case '연고자2 관계': return getAcquaintanceRelationshipDisplay(orderedAcquaintances?.[1]);
        case '연고자2 주소': {
          const roadAddress = orderedAcquaintances?.[1]?.road_address || '';
          const addressDetail = orderedAcquaintances?.[1]?.detail_address || '';
          return [roadAddress, addressDetail].filter(Boolean).join(', ').trim();
        }
        case '연고자2 주민등록번호': return orderedAcquaintances?.[1]?.resident_number || '';
        case '석물비용': {
          // stone_objects의 모든 cost 합계
          const totalStoneCost = (contractData?.stone_objects || []).reduce((sum, stone) => {
            return sum + (parseFloat(stone.cost) || 0);
          }, 0);
          // 각자비(engraving)의 모든 금액 합계 (eng.amount 또는 eng.total_cost)
          const totalEngravingCost = (contractData?.engraving || []).reduce((sum, eng) => {
            const amount = parseFloat(eng.amount || eng.total_cost || 0);
            return sum + amount;
          }, 0);
          const installationFee = parseFloat(contractData?.cost?.installation_fee || 0);
          // 석물 총합 + 각자비 총합 + 설치비
          return formatNumber(totalStoneCost + totalEngravingCost + installationFee);
        }
        case '각자비_메모': {
          // 각자비(engraving)의 모든 금액 합계
          const totalEngravingCost = (contractData?.engraving || []).reduce((sum, eng) => {
            const amount = parseFloat(eng.amount || eng.total_cost || 0);
            return sum + amount;
          }, 0);
          console.log('=== 각자비_메모 처리 ===');
          console.log('contractData.engraving:', contractData?.engraving);
          console.log('totalEngravingCost:', totalEngravingCost);
          // 각자비가 있는 경우에만 메모 작성
          if (totalEngravingCost > 0) {
            const result = `각자비 ${formatNumber(totalEngravingCost)}원`;
            console.log('각자비_메모 결과:', result);
            return result;
          }
          return '';
        }
        default:
          // Stone object fields (석물1~6)
          // stone_objects를 stone_name별로 그룹화하여 처리
          const stoneObjects = contractData?.stone_objects || [];

          // stone_name별로 그룹화 (같은 이름의 석물들을 묶음)
          const groupedStones = stoneObjects.reduce((acc, stone) => {
            const name = stone.stone_name || '';
            if (!acc[name]) {
              acc[name] = {
                stone_name: name,
                count: 0,
                totalCost: 0,
                stones: []
              };
            }
            acc[name].count += 1;
            acc[name].totalCost += parseFloat(stone.cost) || 0;
            acc[name].stones.push(stone);
            return acc;
          }, {});

          // 그룹화된 석물들을 배열로 변환 (정렬: 이름순)
          const groupedStonesArray = Object.values(groupedStones).sort((a, b) =>
            (a.stone_name || '').localeCompare(b.stone_name || '')
          );

          // 각자비(engraving)를 석물 항목에 추가
          // 각자비는 각 항목을 별도로 추가 (품목: 각자 종류, 가격: 금액, 수량: 1)
          const engravingItems = (contractData?.engraving || [])
            .map((eng, idx) => {
              // 각자 종류: eng.type 또는 eng.engraving_type (빈 문자열 처리)
              const engravingType = (eng.type || eng.engraving_type || '').trim() || `각자비${idx + 1}`;
              // 금액: eng.amount 또는 eng.total_cost
              const engravingAmount = parseFloat(eng.amount || eng.total_cost || 0);
              return {
                stone_name: engravingType,
                count: 1, // 각자비는 항목당 1개
                totalCost: engravingAmount,
                isEngraving: true
              };
            })
            .filter(item => item.totalCost > 0); // 금액이 0보다 큰 항목만 포함

          // 설치비가 있으면 석물비용 항목 마지막에 "설치비"를 추가
          const installationFee = parseFloat(contractData?.cost?.installation_fee || 0);
          const installationItems = installationFee > 0 ? [{
            stone_name: '설치비',
            count: 1,
            totalCost: installationFee,
            isInstallationFee: true
          }] : [];

          // 석물 + 각자비 + 설치비를 합친 배열 (설치비는 마지막)
          const allItems = [...groupedStonesArray, ...engravingItems, ...installationItems];

          // 디버깅 로그
          if (fieldId.startsWith('석물')) {
            console.log('=== 석물 필드 처리 ===');
            console.log('fieldId:', fieldId);
            console.log('groupedStonesArray:', groupedStonesArray);
            console.log('engravingItems:', engravingItems);
            console.log('allItems:', allItems);
          }

          if (fieldId.startsWith('석물') && fieldId.includes('품목')) {
            const stoneIndex = fieldId.match(/석물(\d+)/)?.[1];
            if (stoneIndex && allItems[parseInt(stoneIndex) - 1]) {
              return allItems[parseInt(stoneIndex) - 1].stone_name || '';
            }
            return '';
          }
          if (fieldId.startsWith('석물') && fieldId.includes('규격')) {
            // 규격 필드는 현재 stone_objects에 없으므로 빈 문자열 반환
            return '';
          }
          if (fieldId.startsWith('석물') && fieldId.includes('수량')) {
            const stoneIndex = fieldId.match(/석물(\d+)/)?.[1];
            if (stoneIndex && allItems[parseInt(stoneIndex) - 1]) {
              // 같은 stone_name의 개수 누산 (각자비는 항상 1)
              return allItems[parseInt(stoneIndex) - 1].count.toString();
            }
            return '0';
          }
          if (fieldId.startsWith('석물') && fieldId.includes('가격')) {
            const stoneIndex = fieldId.match(/석물(\d+)/)?.[1];
            if (stoneIndex && allItems[parseInt(stoneIndex) - 1]) {
              // 같은 stone_name의 총 비용
              return formatNumber(allItems[parseInt(stoneIndex) - 1].totalCost);
            }
            return '0';
          }
          return '';
      }
    };

    const calculateTotalCost = () => {
      // contractData.cost.total_cost가 있으면 우선 사용 (안치자 추가 계약 모드 등에서 이미 계산된 값)
      if (contractData?.cost?.total_cost !== undefined && contractData?.cost?.total_cost !== null) {
        return parseFloat(contractData.cost.total_cost) || 0;
      }

      // 기존 계산 로직 (일반 계약 모드)
      const usageFee = parseFloat(contractData?.cost?.usage_fee || 0);
      const managementFee = parseFloat(contractData?.cost?.management_fee || 0);
      const burialWorkFee = parseFloat(contractData?.cost?.burial_work_fee || 0);
      const installationFee = parseFloat(contractData?.cost?.installation_fee || 0);
      const engravingFee = parseFloat(contractData?.cost?.engraving_fee || 0);
      return usageFee + managementFee + burialWorkFee + installationFee + engravingFee;
    };

    // Build fields array from template
    // 석물 + 각자비 + 설치비의 총 개수 계산
    const totalStoneAndEngravingCount = (contractData?.stone_objects || []).length +
      (contractData?.engraving || []).filter(eng => parseFloat(eng.amount || eng.total_cost || 0) > 0).length +
      ((parseFloat(contractData?.cost?.installation_fee || 0) > 0) ? 1 : 0);

    console.log('=== 필드 필터링 ===');
    console.log('stone_objects 개수:', (contractData?.stone_objects || []).length);
    console.log('engraving 개수:', (contractData?.engraving || []).filter(eng => parseFloat(eng.amount || eng.total_cost || 0) > 0).length);
    console.log('totalStoneAndEngravingCount:', totalStoneAndEngravingCount);

    console.log('=== 템플릿 field_ids ===');
    console.log('template.field_ids:', template.field_ids);
    console.log('각자비_메모 포함 여부:', template.field_ids.includes('각자비_메모'));

    const fields = template.field_ids
      .filter(fieldId => {
        // '석물비용'은 항상 포함
        if (fieldId === '석물비용') {
          console.log('석물비용 필드 포함');
          return true;
        }
        // '각자비_메모'는 각자비가 있으면 포함
        if (fieldId === '각자비_메모') {
          console.log('각자비_메모 필드 포함 (무조건)');
          return true;
        }
        // Only include stone object fields (석물1~N) if stone objects or engraving exist
        if (fieldId.startsWith('석물')) {
          const stoneIndex = fieldId.match(/석물(\d+)/)?.[1];
          if (stoneIndex) {
            const index = parseInt(stoneIndex);
            // 석물 + 각자비 총 개수 이하면 포함
            const shouldInclude = index <= totalStoneAndEngravingCount;
            console.log(`필드 ${fieldId}: index=${index}, totalCount=${totalStoneAndEngravingCount}, 포함여부=${shouldInclude}`);
            return shouldInclude;
          }
          return false;
        }
        // Only include 연고자2 fields if second acquaintance exists
        if (fieldId.includes('연고자2')) {
          return orderedAcquaintances?.[1] ? true : false;
        }
        return true;
      })
      .map(fieldId => {
        const value = mapFieldValue(fieldId);
        console.log(`필드 매핑: ${fieldId} = "${value}"`);
        return {
          id: fieldId,
          value: value,
          enabled: true,
          required: false
        };
      });

    console.log('=== 최종 fields 배열 ===');
    console.log('총 필드 개수:', fields.length);
    console.log('각자비_메모 필드:', fields.find(f => f.id === '각자비_메모'));

    // Get company ID from backend config
    const companyId = embeddingConfig?.company_id || 'f9aec832efef4133a1e849efaf8a9aed';

    // Get member_id for the cemetery
    const cemeteryConfig = embeddingConfig?.cemeteries?.find(c => c.cemetery_id === contractData?.mausoleum?.cemetery_id);
    const memberId = cemeteryConfig?.member_id || 'nhgpark1973@naver.com';

    const document_option = {
      company: {
        id: companyId,
        country_code: 'kr',
        user_key: memberId
      },
      user: accessToken ? {
        type: '01', // 멤버
        id: memberId,
        access_token: accessToken,
        refresh_token: refreshToken // Refresh token 사용
      } : {
        type: '01', // Access Token 없으면 로그인 화면 표시
        id: memberId
      },
      mode: {
        type: '01', // 새 문서 작성
        template_id: template.template_id
      },
      layout: {
        lang_code: 'ko',
        header: true,
        footer: true
      },
      prefill: {
        document_name: template.template_name,
        fields: fields
      }
    };

    console.log('document_option:', document_option);
    console.log('fields count:', fields.length);
    console.log('fields:', fields);

    const success_callback = function (response) {
      console.log('✅✅✅ Document preview successful!');
      console.log('Success response:', response);
      console.log('Response code:', response.code);
      console.log('Document ID:', response.document_id);
    };

    const error_callback = function (response) {
      console.error('❌❌❌ Document preview failed!');
      console.error('Error response:', response);
      console.error('Error code:', response.code);
      console.error('Error message:', response.message);
      alert('문서 미리보기 중 오류가 발생했습니다:\nCode: ' + response.code + '\nMessage: ' + (response.message || '알 수 없는 오류'));
    };

    const action_callback = function (response) {
      console.log('📋 Document action:', response);
    };

    try {
      console.log('Calling eformsignRef.current.document()...');

      // iframe 확인
      const iframe = document.getElementById('eformsign_iframe');
      console.log('iframe element:', iframe);
      console.log('iframe style:', iframe?.style);
      console.log('iframe offsetWidth:', iframe?.offsetWidth);
      console.log('iframe offsetHeight:', iframe?.offsetHeight);

      eformsignRef.current.document(
        document_option,
        'eformsign_iframe',
        success_callback,
        error_callback,
        action_callback
      );
      console.log('Calling eformsignRef.current.open()...');
      eformsignRef.current.open();
      console.log('eformsignRef.current.open() called successfully');

      // open 후 iframe 상태 재확인
      setTimeout(() => {
        const iframeAfter = document.getElementById('eformsign_iframe');
        console.log('=== iframe 상태 (open 후) ===');
        console.log('iframe src:', iframeAfter?.src);
        console.log('iframe contentWindow:', iframeAfter?.contentWindow);
        console.log('iframe innerHTML:', iframeAfter?.innerHTML);
      }, 1000);

    } catch (error) {
      console.error('❌ Failed to open document preview:', error);
      alert('문서 미리보기를 열 수 없습니다: ' + error.message);
      setIsPreviewing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content w-11/12 md:w-4/5 lg:w-3/4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">
            {isPreviewing && selectedTemplate ? '전자 계약서 미리보기' : '계약서 템플릿 선택'}
          </h2>
          <button onClick={onClose} className="btn btn-ghost btn-sm">
            <X size={20} />
          </button>
        </div>

        {isPreviewing ? null : (
          <div className="flex-1 overflow-y-auto">
            {templates.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>사용 가능한 템플릿이 없습니다.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {templates.map((template) => (
                  <div
                    key={template.template_id}
                    className="card bg-base-100 shadow-md cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => handleSelectTemplate(template)}
                  >
                    <div className="card-body p-4">
                      <h3 className="card-title text-lg">{template.template_name}</h3>
                      <p className="text-sm text-gray-500">필드 수: {template.field_ids.length}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="modal-action mt-6">
          <button
            onClick={() => {
              if (isPreviewing) {
                setIsPreviewing(false);
                if (eformsignRef.current) {
                  try {
                    eformsignRef.current.close();
                  } catch (e) {
                    console.log('eFormSign already closed');
                  }
                }
              } else {
                onClose();
              }
            }}
            className="btn btn-outline w-full"
          >
            {isPreviewing ? '템플릿 선택으로 돌아가기' : '닫기'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ElectronicContractModal;

